import Icon from '../../../components/AppIcon';
import { Achievement } from '../types';

interface AchievementBadgesProps {
  achievements: Achievement[];
}

const AchievementBadges = ({ achievements }: AchievementBadgesProps) => {
  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    }).format(date);
  };

  const getCategoryColor = (category: Achievement['category']) => {
    switch (category) {
      case 'consistency':
        return { bg: 'bg-success/10', text: 'text-success', border: 'border-success/20' };
      case 'milestone':
        return { bg: 'bg-warning/10', text: 'text-warning', border: 'border-warning/20' };
      case 'participation':
        return { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/20' };
      default:
        return { bg: 'bg-muted', text: 'text-muted-foreground', border: 'border-border' };
    }
  };

  return (
    <div className="bg-card rounded-md border border-border p-6 shadow-elevation-1">
      <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
        <Icon name="Trophy" size={20} />
        Achievements ({achievements.length})
      </h2>

      {achievements.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <Icon name="Award" size={32} className="text-muted-foreground" />
          </div>
          <p className="text-muted-foreground">No achievements earned yet</p>
          <p className="text-sm text-muted-foreground mt-1">
            Keep contributing to unlock achievements!
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {achievements.map((achievement) => {
            const colors = getCategoryColor(achievement.category);
            return (
              <div
                key={achievement.id}
                className={`p-4 rounded-sm border ${colors.border} ${colors.bg} transition-interactive hover-scale`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-full ${colors.bg} border-2 ${colors.border} flex items-center justify-center flex-shrink-0`}>
                    <Icon name={achievement.icon} size={24} color={colors.text.replace('text-', 'var(--color-')} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-foreground mb-1">
                      {achievement.title}
                    </h3>
                    <p className="text-xs text-muted-foreground capitalize">
                      {achievement.category}
                    </p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mb-3">
                  {achievement.description}
                </p>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Icon name="Calendar" size={14} />
                  <span>Earned {formatDate(achievement.earnedDate)}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AchievementBadges;